/**
 * MidiExporter.js
 * Utility to generate binary MIDI files (Type 1) from the application's event data.
 * Ported and adapted from legacy MidiEngine.js.
 */

export const exportToMidi = (events, bpm = 120, tempoMap = []) => {
    if (!events || events.length === 0) return null;

    const writeChunk = (id, data) => {
        const len = data.length;
        const idBytes = id.split('').map(c => c.charCodeAt(0));
        const lenBytes = [(len >> 24) & 0xFF, (len >> 16) & 0xFF, (len >> 8) & 0xFF, len & 0xFF];
        return idBytes.concat(lenBytes).concat(data);
    };

    const writeVarInt = (val) => {
        if (val < 0) val = 0;
        if (val === 0) return [0];
        const chunks = [];
        let temp = val;
        do {
            chunks.push(temp & 0x7F);
            temp >>= 7;
        } while (temp > 0);
        const bytes = [];
        for (let i = chunks.length - 1; i >= 0; i--) {
            let byte = chunks[i];
            if (i > 0) byte |= 0x80;
            bytes.push(byte);
        }
        return bytes;
    };

    // Use instrumentId if available to group, otherwise role.
    // Studio One prefers one track per instrument.
    const getTrackKey = (e) => e.instrumentId || e.role;
    const trackKeys = [...new Set(events.map(getTrackKey))];

    // Sort keys to put 'melody' etc first? No, reliable order.
    trackKeys.sort();

    const trackCount = trackKeys.length + 1; // +1 for Tempo/Header track
    const ticksPerBeat = 480;

    // 1. Header Chunk
    const headerData = [0, 1, (trackCount >> 8) & 0xFF, trackCount & 0xFF, (ticksPerBeat >> 8) & 0xFF, ticksPerBeat & 0xFF];
    const header = writeChunk('MThd', headerData);

    // 2. Tempo Track
    // Convert tempoMap to MIDI events
    let tempoEvents = [];

    // Default initial tempo if map empty
    if (tempoMap.length === 0) {
        tempoMap.push({ tick: 0, bpm: bpm });
    }

    tempoMap.forEach(tm => {
        const microseconds = Math.round(60000000 / tm.bpm);
        tempoEvents.push({
            t: tm.tick,
            type: 0xFF, // Meta
            metaType: 0x51, // Set Tempo
            data: [(microseconds >> 16) & 0xFF, (microseconds >> 8) & 0xFF, microseconds & 0xFF]
        });
    });

    // End of Track
    const lastEventTime = events.reduce((max, e) => Math.max(max, (e.s + e.len) * ticksPerBeat), 0);
    const lastTick = Math.max(lastEventTime, tempoEvents[tempoEvents.length-1].t);
    tempoEvents.push({ t: lastTick, type: 0xFF, metaType: 0x2F, data: [] });

    // Sort and Write Tempo Track
    tempoEvents.sort((a,b) => a.t - b.t);

    const tempoTrackBytes = [];
    let lastTempoTick = 0;

    tempoEvents.forEach(ev => {
        const delta = ev.t - lastTempoTick;
        lastTempoTick = ev.t;

        tempoTrackBytes.push(...writeVarInt(delta));
        tempoTrackBytes.push(ev.type, ev.metaType);

        const dataLen = ev.data ? ev.data.length : 0;
        tempoTrackBytes.push(...writeVarInt(dataLen));

        if (ev.data) tempoTrackBytes.push(...ev.data);
    });

    const tracks = [writeChunk('MTrk', tempoTrackBytes)];

    // 3. Instrument Tracks
    if (!trackKeys) return new Uint8Array([]);

    trackKeys.forEach(key => {
        // Filter events for this track
        const trackEvents = events.filter(e => getTrackKey(e) === key).sort((a,b) => a.s - b.s);

        let lastTick = 0;
        let trackData = [];
        let midiEvents = [];

        // Determine Channel
        // Use event channel if available, else derive from role/index
        let channel = 0;
        if (trackEvents.length > 0 && trackEvents[0].channel !== undefined) {
            channel = trackEvents[0].channel - 1; // 1-based to 0-based
        }
        if (channel < 0) channel = 0;

        // Keyswitch injection optimization:
        // Group by time to insert keyswitch BEFORE the note cluster starts.
        // Actually, easiest is just inject KS event slightly earlier.

        trackEvents.forEach(e => {
            const isNote = e.d !== undefined;
            const isCC = e.type === 'cc';

            // Support Playback Offset (Groove/Micro-timing) without altering grid
            const time = e.s + (e.playbackOffset || 0);
            const startTick = Math.round(time * ticksPerBeat);

            if (isNote) {
                const noteNum = Math.round(e.d);
                if (noteNum < 0 || noteNum > 127) return;

                const endTick = startTick + Math.round(e.len * ticksPerBeat);
                const velocity = Math.min(127, Math.max(1, Math.round((e.vel || 0.8) * 127)));

                // Keyswitch Injection (Enhancement #5)
                // Info: this logic injects physical notes for Keyswitches.
                // This is correct for DAW export but distinct from the internal AudioEngine
                // which handles keyswitches as state changes without playing notes.
                const ksVal = (e.ks !== undefined) ? e.ks : e.keyswitch;

                if (ksVal !== undefined && ksVal !== null) {
                    // Constraint Fix: Configurable KS Offset
                    // Default to 10 ticks if not provided in event or config
                    const ksOffset = e.ksOffset || 10;
                    const ksVelocity = e.ksVelocity || 10;

                    // Inject KS before note
                    const ksTick = Math.max(0, startTick - ksOffset);

                    // Standard: Note On -> Note Off (short blip)
                    midiEvents.push({ t: ksTick, type: 0x90, n: ksVal, v: ksVelocity, ch: channel });
                    midiEvents.push({ t: ksTick + 5, type: 0x80, n: ksVal, v: 0, ch: channel });
                }

                midiEvents.push({ t: startTick, type: 0x90, n: noteNum, v: velocity, ch: channel });
                midiEvents.push({ t: endTick, type: 0x80, n: noteNum, v: 0, ch: channel });
            } else if (isCC) {
                const val = Math.min(127, Math.max(0, Math.round(e.val * 127)));
                midiEvents.push({ t: startTick, type: 0xB0, n: e.cc, v: val, ch: channel });
            }
        });

        // Sort events by time to calculate deltas
        midiEvents.sort((a,b) => {
            if (a.t === b.t) {
                // Order: Keyswitch On -> Keyswitch Off -> CC -> Note On -> Note Off
                // Priority:
                // KS Note On (0x90, low vel?) - Hard to distinguish.
                // Let's rely on time.
                return 0;
            }
            return a.t - b.t;
        });

        // Write events
        midiEvents.forEach(ev => {
            const delta = ev.t - lastTick;
            lastTick = ev.t;
            // Removed spread: trackData.push(...writeVarInt(delta));

            // Running Status? Skipping for safety/simplicity.
            // Status: Type | Channel
            const status = ev.type | (ev.ch & 0x0F);

            const deltaBytes = writeVarInt(delta);
            for (let i = 0; i < deltaBytes.length; i++) trackData.push(deltaBytes[i]);
            trackData.push(status, ev.n, ev.v);
        });

        // End of Track
        trackData.push(0x00, 0xFF, 0x2F, 0x00);
        tracks.push(writeChunk('MTrk', trackData));
    });

    let fileBytes = header;
    for (const track of tracks) {
        fileBytes = fileBytes.concat(track);
    }
    return new Uint8Array(fileBytes);
};
